import { useEffect, useMemo, useRef, useState } from 'react';

export interface PaletteItem {
    key: string;
    label: string;
    hint?: string;          // grupo del menú al que pertenece la pantalla
    icon: string;
    path: string;
    kind: 'page' | 'action';
}

interface Props {
    open: boolean;
    items: PaletteItem[];
    onClose: () => void;
    onSelect: (item: PaletteItem) => void;
}

// Búsqueda sin distinguir mayúsculas ni tildes
const normalize = (text?: string | null) =>
    (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const SECTIONS: { kind: PaletteItem['kind']; title: string }[] = [
    { kind: 'action', title: 'Acciones' },
    { kind: 'page', title: 'Pantallas' },
];

export const isMacPlatform = () => /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

/**
 * Paleta de comandos (Ctrl/⌘ + K): buscador flotante para ir a cualquier pantalla del menú o
 * ejecutar una acción rápida. Se maneja con el teclado (↑ ↓ Enter Esc).
 */
export const CommandPalette = ({ open, items, onClose, onSelect }: Props) => {
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Cada vez que se abre empieza vacía
    useEffect(() => {
        if (!open) return;
        setQuery('');
        setActive(0);
        setTimeout(() => inputRef.current?.focus(), 0);
    }, [open]);

    // Coincidencias: primero las que empiezan con el texto, luego las que lo contienen (nombre o grupo)
    const results = useMemo(() => {
        const term = normalize(query);
        const scored = items
            .map(item => {
                if (!term) return { item, score: 0 };
                const label = normalize(item.label);
                const hint = normalize(item.hint);
                if (label.startsWith(term)) return { item, score: 3 };
                if (label.split(/\s+/).some(word => word.startsWith(term))) return { item, score: 2 };
                if (label.includes(term) || hint.includes(term)) return { item, score: 1 };
                return null;
            })
            .filter(Boolean) as { item: PaletteItem; score: number }[];
        // Se agrupan por sección para que el orden en pantalla y el de las flechas coincidan
        return SECTIONS.flatMap(section => scored
            .filter(s => s.item.kind === section.kind)
            .sort((a, b) => b.score - a.score)
            .map(s => s.item));
    }, [items, query]);

    useEffect(() => { setActive(0); }, [query]);

    // La opción activa siempre visible al moverse con las flechas
    useEffect(() => {
        listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    if (!open) return null;

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive(i => (results.length === 0 ? 0 : (i + 1) % results.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(i => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[active]) onSelect(results[active]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    let index = -1;
    return (
        <div className="app-palette-backdrop" onMouseDown={onClose}>
            <div className="app-palette" role="dialog" aria-modal="true" aria-label="Ir a una pantalla o acción"
                onMouseDown={e => e.stopPropagation()}>
                <div className="app-palette-search">
                    <i className="bi bi-search" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="¿A dónde quiere ir? Escriba una pantalla o acción..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        aria-label="Buscar pantalla o acción"
                        aria-activedescendant={results[active] ? `palette-${results[active].key}` : undefined}
                    />
                    <kbd>Esc</kbd>
                </div>

                <div className="app-palette-list" ref={listRef} role="listbox">
                    {results.length === 0 && (
                        <div className="app-palette-empty">Sin resultados para “{query}”</div>
                    )}
                    {SECTIONS.map(section => {
                        const sectionItems = results.filter(r => r.kind === section.kind);
                        if (sectionItems.length === 0) return null;
                        return (
                            <div key={section.kind}>
                                <div className="app-palette-section">{section.title}</div>
                                {sectionItems.map(item => {
                                    index += 1;
                                    const itemIndex = index;
                                    return (
                                        <button
                                            key={item.key}
                                            id={`palette-${item.key}`}
                                            type="button"
                                            role="option"
                                            aria-selected={itemIndex === active}
                                            data-index={itemIndex}
                                            className={`app-palette-item ${itemIndex === active ? 'active' : ''}`}
                                            onMouseMove={() => setActive(itemIndex)}
                                            onClick={() => onSelect(item)}
                                        >
                                            <span className="app-palette-icon"><i className={`bi ${item.icon}`} /></span>
                                            <span className="app-palette-label">{item.label}</span>
                                            {item.hint && <span className="app-palette-hint">{item.hint}</span>}
                                            <i className="bi bi-arrow-return-left app-palette-enter" />
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                <div className="app-palette-footer">
                    <span><kbd>↑</kbd><kbd>↓</kbd> moverse</span>
                    <span><kbd>Enter</kbd> abrir</span>
                    <span><kbd>{isMacPlatform() ? '⌘' : 'Ctrl'}</kbd><kbd>K</kbd> abrir o cerrar</span>
                </div>
            </div>
        </div>
    );
};
